import Button from "@rio-cloud/rio-uikit/Button"
import ExpanderPanel from "@rio-cloud/rio-uikit/ExpanderPanel"
import Notification from "@rio-cloud/rio-uikit/Notification"
import NoData from "@rio-cloud/rio-uikit/NoData"
import Sidebar from "@rio-cloud/rio-uikit/Sidebar"
import useClipboard from "@rio-cloud/rio-uikit/useClipboard"
import { ReactNode, useEffect } from "react"
import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout"

import { formatDateColumn, formatDateTimeColumn } from "@/helper/formatHelper"
import { Address, Shipment, TimeWindow } from "@/model/transportorder/transportOrder"
import { getSelectedAdminTransportOrder } from "@/redux/admin-ui.selector"
import { adminUiSlice } from "@/redux/admin-ui.slice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { getAdminConsigneeDetails, getAdminConsignorDetails, TransportChainPartyDetails } from "./transportChainDetails"

export const SelectedTransportOrderSidebar = () => {
    const dispatch = useAppDispatch()
    const selectedTransportOrder = useAppSelector(getSelectedAdminTransportOrder)

    return (
        <ApplicationLayout.Sidebar className='right'>
            <Sidebar
                title='Transport order details'
                titleClassName='padding-left-10'
                closed={!selectedTransportOrder}
                onClose={() => dispatch(adminUiSlice.actions.setSelectedTransportOrder(undefined))}
                position={Sidebar.RIGHT}
                switchModeBreakpoint={1400}
                width={420}
            >
                {selectedTransportOrder && <SelectedTransportOrderSidebarContent transportOrder={selectedTransportOrder} />}
            </Sidebar>
        </ApplicationLayout.Sidebar>
    )
}

export const SelectedTransportOrderSidebarContent = (props: { transportOrder: Shipment }) => {
    const { transportOrder } = props
    const clipboard = useClipboard()
    const consignor = getAdminConsignorDetails(transportOrder)
    const consignee = getAdminConsigneeDetails(transportOrder)

    useEffect(() => {
        clipboard.copied && Notification.default("Text copied to clipboard")
    }, [clipboard.copied])

    return (
        <div className='height-100pct display-flex flex-column' data-testid='RioAdmin_SelectedTransportOrderSidebar'>
            <SelectedTransportOrderSidebarHeader />

            <div className='bg-white padding-y-20 flex-1-1'>
                <SidebarSection title='Overview'>
                    <SidebarDetailRow label='Transport order number' value={<CopyableValue value={transportOrder.external_identification} onCopy={clipboard.copy} />} />
                    <SidebarDetailRow label='Transport order ID' value={<CopyableValue value={transportOrder.id} onCopy={clipboard.copy} />} />
                    <SidebarDetailRow label='Bundle ID' value={<CopyableValue value={transportOrder.transport_order_id} onCopy={clipboard.copy} />} />
                    <SidebarDetailRow label='Brand' value={transportOrder.brand} />
                    <SidebarDetailRow label='Priority' value={transportOrder.priority} />
                    <SidebarDetailRow label='Process' value={transportOrder.processIndicator} />
                    <SidebarDetailRow label='Status' value={transportOrder.status} />
                    <SidebarDetailRow label='Created' value={formatDateColumn(transportOrder.createdDate)} />
                    <SidebarDetailRow label='Last modified' value={formatDateColumn(transportOrder.last_modified_date)} />
                    <SidebarDetailRow label='Confirmed' value={transportOrder.isConfirmed ? "Yes" : "No"} />
                    <SidebarDetailRow label='Confirmed At' value={formatDateColumn(transportOrder.confirmationDraft.updatedAt)} />
                    <SidebarDetailRow label='Two drivers' value={transportOrder.requiresTwoDrivers ? "Yes" : "No"} />
                    <SidebarDetailRow label='Vehicle type' value={transportOrder.proposedVehicleType} withBottomBorder={false} />
                </SidebarSection>

                <SidebarSection title='Parties'>
                    <SidebarDetailRow
                        label='Freight forwarder'
                        value={
                            <PartyDetails
                                name={transportOrder.freightForwarder.name}
                                identifiers={[
                                    ["Account", transportOrder.freightForwarder.accountId],
                                    ["DUNS", transportOrder.freightForwarder.duns],
                                    ["Supplier code", transportOrder.freightForwarder.supplierCode],
                                ]}
                                address={transportOrder.freightForwarder.address}
                            />
                        }
                    />
                    <SidebarDetailRow
                        label='Supplier'
                        value={
                            <PartyDetails
                                name={transportOrder.supplier.name}
                                identifiers={[
                                    ["DUNS", transportOrder.supplier.externalIdentifiers.duns],
                                    ["Plant", transportOrder.supplier.externalIdentifiers.plantCode],
                                    ["Supplier code", transportOrder.supplier.externalIdentifiers.supplierCode],
                                ]}
                                address={transportOrder.supplier.address}
                            />
                        }
                    />
                    <SidebarDetailRow
                        label='Buyer'
                        value={<PartyDetails name={transportOrder.buyer?.name} address={transportOrder.buyer?.address} />}
                        withBottomBorder={false}
                    />
                </SidebarSection>

                <SidebarSection title='Route'>
                    <SidebarDetailRow label='Consignor' value={<StopDetails stop={consignor} />} />
                    <SidebarDetailRow label='Consignee' value={<StopDetails stop={consignee} />} withBottomBorder={false} />
                </SidebarSection>

                <SidebarSection title='Freight'>
                    <SidebarDetailRow label='Type' value={transportOrder.freight.type} />
                    <SidebarDetailRow label='Gross weight' value={formatMeasurement(transportOrder.freight.gross_weight)} />
                    <SidebarDetailRow label='Gross volume' value={formatMeasurement(transportOrder.freight.gross_volume)} withBottomBorder={false} />
                </SidebarSection>
            </div>
        </div>
    )
}

const SelectedTransportOrderSidebarHeader = () => (
    <div className='display-flex justify-content-center'>
        <div className='flex-1-1 text-center'>
            <Button
                bsStyle={Button.PRIMARY}
                variant={Button.VARIANT_ACTION}
                iconName='rioglyph-delivery'
                className='text-size-h5'
                noRippleEffect
                tabIndex={-1}
            >
                Transport order
            </Button>
        </div>
    </div>
)

const SidebarSection = (props: { title: string, children: ReactNode }) => (
    <ExpanderPanel title={props.title} bsStyle='default' className='margin-x-20 margin-bottom-20' bodyClassName='padding-0' open>
        {props.children}
    </ExpanderPanel>
)

const SidebarDetailRow = (props: { label: string, value: ReactNode, withBottomBorder?: boolean }) => {
    const { label, value, withBottomBorder = true } = props
    const singleBottomBorderClassName = "border-style-solid border-width-1 border-color-light border-right-0 border-top-0 border-left-0"
    const rowClassName = "display-flex padding-10 padding-top-5 padding-bottom-5"
        + (withBottomBorder ? " " + singleBottomBorderClassName : "")

    return (
        <div className={rowClassName}>
            <div className='width-40pct text-color-dark padding-y-5 padding-right-10 padding-left-5'>
                {label}
            </div>
            <div className='width-60pct margin-left-5 margin-right-5 padding-y-5 overflow-hidden'>
                {value || <SidebarNoData />}
            </div>
        </div>
    )
}

const SidebarNoData = () => <NoData text='No data' />

const CopyableValue = (props: { value?: string, onCopy: (value: string) => void }) => {
    if (!props.value) {
        return <SidebarNoData />
    }

    return (
        <div className='display-flex justify-content-between align-items-center width-100pct'>
            <span className='margin-right-5 ellipsis-1 word-break-all'>{props.value}</span>
            <Button
                bsStyle='muted'
                bsSize='xs'
                className='hover-bg-none'
                onClick={() => props.onCopy(props.value ?? "")}
                iconName='rioglyph-duplicate'
                iconOnly
            />
        </div>
    )
}

const PartyDetails = (props: {
    name?: string,
    identifiers?: Array<[string, string | undefined]>,
    address?: Address,
}) => (
    <div className='text-size-14 overflow-hidden'>
        {props.name ? <div className='text-medium margin-bottom-5 ellipsis-1'>{props.name}</div> : <SidebarNoData />}
        {props.identifiers?.map(([label, value]) => value ? <div key={label} className='text-color-dark ellipsis-1'>{label}: {value}</div> : null)}
        <AddressDetails address={props.address} />
    </div>
)

const StopDetails = (props: { stop?: TransportChainPartyDetails }) => {
    const { stop } = props

    if (!stop) {
        return <SidebarNoData />
    }

    return (
        <div className='text-size-14 overflow-hidden'>
            <div className='text-medium margin-bottom-5 ellipsis-1'>{stop.name}</div>
            {stop.duns && <div className='text-color-dark ellipsis-1'>DUNS: {stop.duns}</div>}
            <AddressDetails address={stop.address} />
            <TimeWindowDetails timeWindow={stop.timeWindow} />
        </div>
    )
}

const AddressDetails = (props: { address?: Address }) => {
    if (!props.address) {
        return null
    }

    return (
        <>
            <div className='text-color-dark ellipsis-1'>{props.address.street} {props.address.house_number}</div>
            <div className='text-color-dark ellipsis-1'>{props.address.city} {props.address.postal_code}</div>
            <div className='text-color-dark ellipsis-1'>{props.address.country}</div>
        </>
    )
}

const TimeWindowDetails = (props: { timeWindow?: TimeWindow }) => {
    if (!props.timeWindow?.time_from && !props.timeWindow?.time_to) {
        return null
    }

    return (
        <div className='margin-top-5'>
            <div className='text-medium margin-bottom-5'>Time window</div>
            <div className='text-color-dark'>From: {props.timeWindow?.time_from ? formatDateTimeColumn(props.timeWindow.time_from) : <SidebarNoData />}</div>
            <div className='text-color-dark'>To: {props.timeWindow?.time_to ? formatDateTimeColumn(props.timeWindow.time_to) : <SidebarNoData />}</div>
        </div>
    )
}

const formatMeasurement = (measurement?: { value?: number, unit: string }) => {
    if (!measurement || measurement.value === undefined) {
        return undefined
    }

    return `${measurement.value} ${measurement.unit}`
}

